import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { UserPlus, Shield, Clock, Monitor, Trash2, Copy, Share2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const MODULES = [
  { id: 'calendar', label: 'Calendario' }, { id: 'tasks', label: 'Tareas' }, { id: 'projects', label: 'Proyectos' },
  { id: 'finance', label: 'Finanzas' }, { id: 'notes', label: 'Notas' }, { id: 'clients', label: 'Clientes' },
];
const ACTIONS = ['view', 'create', 'edit', 'delete'];
const ALABEL = { view: 'Ver', create: 'Crear', edit: 'Editar', delete: 'Eliminar' };
const blankPerms = () => Object.fromEntries(MODULES.map((m) => [m.id, { view: true, create: false, edit: false, delete: false }]));

export default function AssistantsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('team');
  const [assistants, setAssistants] = useState([]);
  const [audit, setAudit] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', password: 'assistant123', permissions: blankPerms(), expires_at: '' });

  const load = async () => {
    const [a, au] = await Promise.all([api.get('/assistants'), api.get('/audit').catch(() => ({ data: [] }))]);
    setAssistants(a.data); setAudit(au.data);
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email || !form.name) return toast.error('Nombre y email requeridos');
    try {
      const { data } = await api.post('/assistants/invite', { ...form, expires_at: form.expires_at || null });
      toast.success('Asistente invitado');
      if (data.temp_password) toast.info(`Contraseña temporal: ${data.temp_password}`, { duration: 8000 });
      setOpen(false); setForm({ email: '', name: '', password: 'assistant123', permissions: blankPerms(), expires_at: '' }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const togglePerm = (m, mod, act) => {
    const perms = { ...(m ? m.permissions : form.permissions) };
    perms[mod] = { ...perms[mod], [act]: !perms[mod]?.[act] };
    if (m) { setEditing({ ...m, permissions: perms }); } else { setForm({ ...form, permissions: perms }); }
  };
  const savePerms = async () => { await api.patch(`/assistants/${editing.id}`, { permissions: editing.permissions, expires_at: editing.expires_at || null }); toast.success('Permisos actualizados'); setEditing(null); load(); };
  const remove = async (id) => { await api.delete(`/assistants/${id}`); load(); toast.success('Asistente removido'); };

  const PermGrid = ({ perms, onToggle }) => (
    <div className="space-y-2">
      {MODULES.map((mod) => (
        <div key={mod.id} className="flex items-center gap-2 py-1">
          <span className="text-sm w-24">{mod.label}</span>
          <div className="flex gap-3 flex-wrap">
            {ACTIONS.map((act) => (
              <label key={act} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Switch checked={!!perms[mod.id]?.[act]} onCheckedChange={() => onToggle(mod.id, act)} data-testid={`perm-${mod.id}-${act}`} />
                {ALABEL[act]}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div data-testid="assistants-module">
      <PageHeader kicker="Colaboración" title="Asistentes & Auditoría" subtitle="Permisos granulares por módulo, acceso temporal y logs inmutables."
        actions={<button data-testid="invite-assistant-btn" onClick={() => setOpen(true)} className="h-10 px-4 rounded-xl text-sm font-medium text-[#07090A] flex items-center gap-2" style={{ background: '#3EB489' }}><UserPlus size={16} /> Invitar</button>} />

      <div className="pp-glass p-4 mb-4 flex items-center gap-3 flex-wrap" data-testid="assistants-network-hint">
        <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'rgba(201,168,76,0.14)' }}><Share2 size={18} className="pp-gold" /></span>
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-semibold">Invita asistentes con el botón "Invitar" y define permisos por módulo (ver/crear/editar/eliminar).</div>
          <div className="text-xs text-muted-foreground">¿Quieres vincular otra empresa/cuenta (Padre/Hija, solo lectura)? Ve a Red / Cuentas.</div>
        </div>
        <button data-testid="goto-network" onClick={() => navigate('/plannerapp/network')} className="h-9 px-4 rounded-xl text-sm border flex items-center gap-1.5 pp-gold">Ir a Red / Cuentas <ArrowRight size={14} /></button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4"><TabsList className="pp-glass"><TabsTrigger value="team">Equipo</TabsTrigger><TabsTrigger value="audit">Auditoría</TabsTrigger></TabsList></Tabs>

      {tab === 'team' ? (
        <div className="grid md:grid-cols-2 gap-4">
          {assistants.map((a) => (
            <div key={a.id} className="pp-glass p-5" data-testid={`assistant-${a.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <Avatar className="w-11 h-11"><AvatarFallback className="bg-[rgba(62,180,137,0.2)] pp-mint">{a.assistant_name?.[0]}</AvatarFallback></Avatar>
                <div className="flex-1"><div className="font-semibold">{a.assistant_name}</div><div className="text-xs text-muted-foreground">{a.assistant_email}</div></div>
                <button onClick={() => remove(a.id)} className="text-red-400"><Trash2 size={16} /></button>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {MODULES.map((m) => { const p = a.permissions?.[m.id] || {}; const on = p.view || p.create || p.edit; return (
                  <span key={m.id} className={`text-[10px] px-2 py-0.5 rounded-full ${on ? 'pp-mint border border-[rgba(62,180,137,0.3)]' : 'text-muted-foreground border border-[rgba(255,255,255,0.08)] opacity-50'}`}>{m.label}</span>
                ); })}
              </div>
              {a.expires_at && <div className="text-[11px] pp-gold flex items-center gap-1 mb-2"><Clock size={11} /> Expira {a.expires_at.slice(0, 10)}</div>}
              <button data-testid={`edit-perms-${a.id}`} onClick={() => setEditing({ ...a, expires_at: a.expires_at || '' })} className="text-xs px-3 h-8 rounded-lg border flex items-center gap-1"><Shield size={12} /> Editar permisos</button>
            </div>
          ))}
          {assistants.length === 0 && <div className="pp-glass p-8 text-center text-muted-foreground col-span-full">Sin asistentes. Invita a tu primer colaborador.</div>}
        </div>
      ) : (
        <div className="pp-glass p-5">
          <div className="text-xs text-muted-foreground mb-3">Registro inmutable (append-only) de todas las acciones de tus asistentes.</div>
          <div className="space-y-1 max-h-[60vh] overflow-auto pp-scroll font-mono text-xs">
            {audit.map((l) => (
              <div key={l.id} className="flex items-center gap-3 py-2 border-b border-[rgba(255,255,255,0.04)]">
                <span className="pp-mint">{l.action}</span>
                <span className="text-muted-foreground">{l.module}</span>
                <span className="flex-1 truncate text-muted-foreground">{l.assistant_name}</span>
                <span className="flex items-center gap-1 text-muted-foreground"><Monitor size={10} /> {l.ip || '—'}</span>
                <span className="text-muted-foreground">{new Date(l.timestamp).toLocaleString('es')}</span>
              </div>
            ))}
            {audit.length === 0 && <div className="text-center text-muted-foreground py-6">Sin actividad registrada aún.</div>}
          </div>
        </div>
      )}

      {/* Invite dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="pp-glass-strong max-w-lg"><DialogHeader><DialogTitle>Invitar asistente</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-auto pp-scroll">
            <input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <input data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <div><div className="text-xs text-muted-foreground mb-1">Acceso hasta (opcional)</div><input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none" /></div>
            <div className="pt-2"><div className="text-sm font-semibold mb-2">Permisos granulares</div><PermGrid perms={form.permissions} onToggle={(m, a) => togglePerm(null, m, a)} /></div>
            <button data-testid="invite-submit" onClick={invite} className="w-full h-11 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>Invitar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit perms dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="pp-glass-strong max-w-lg"><DialogHeader><DialogTitle>Permisos de {editing?.assistant_name}</DialogTitle></DialogHeader>
          {editing && <div className="space-y-3 max-h-[65vh] overflow-auto pp-scroll">
            <div><div className="text-xs text-muted-foreground mb-1">Acceso hasta (opcional)</div><input type="date" value={editing.expires_at || ''} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none" /></div>
            <PermGrid perms={editing.permissions || {}} onToggle={(m, a) => togglePerm(editing, m, a)} />
            <button data-testid="save-perms" onClick={savePerms} className="w-full h-11 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>Guardar</button>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
